import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { ok } from 'neverthrow';
import { useEffect, useRef, useState } from 'react';
import {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
} from '@cotera/client/v0/actions/framework';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import posthog from 'posthog-js';
import { Loading } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import type { IconName } from '@cotera/client/app/components/ui/icon/mapping';
import { cn } from '@cotera/client/v0/lib/utils';

const V1_OVERRIDE_KEY = 'COTERA_V1_OVERRIDE';

const getV1OverrideKey = (userId?: string): string => {
  if (userId === undefined) {
    return V1_OVERRIDE_KEY;
  }
  return `${V1_OVERRIDE_KEY}:${userId}`;
};

export type V1OverrideValue = 'enabled' | 'disabled' | null;

export const getV1Override = (userId?: string): V1OverrideValue => {
  const value = localStorage.getItem(getV1OverrideKey(userId));
  if (value === 'enabled' || value === 'disabled') {
    return value;
  }
  return null;
};

export const setV1Override = (
  value: V1OverrideValue,
  userId?: string
): void => {
  const key = getV1OverrideKey(userId);
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
};

type AppVersion = 'v1' | 'classic';

type Params = {
  version: AppVersion;
};

export class SwitchAppVersionAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Switch App Version';
  description = 'Switch between V1 and Classic app versions';
  inputSchema = z.object({
    version: z.enum(['v1', 'classic']),
  });
  group = 'app';
  icon = 'repeat';
  /**
   * Pinned above the alphabetical run. Without a priority it ties at 0 with
   * every other action and sorts under "S", which in a forty-row list put it
   * below the fold — the reason it read as missing entirely.
   */
  priority = 20;

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    },
    private readonly currentVersion: AppVersion,
    private readonly userId: string
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    if (payload.version !== this.currentVersion) {
      // Capture before reload — the normal track() path loses the event to
      // the page teardown. Flush synchronously so it survives the reload.
      posthog.capture(CocoEvent.AppVersionSwitched, {
        from: this.currentVersion,
        to: payload.version,
      });
    }
    if (payload.version === 'classic') {
      setV1Override('disabled', this.userId);
    } else {
      setV1Override('enabled', this.userId); // Force V1 (overrides legacy-app-access)
    }
    window.location.reload();
    return ok({});
  }

  ask(_params: Partial<Params>, _context: ExecuteContext): AskResponse<Params> {
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <SwitchAppVersionAskView
              currentVersion={this.currentVersion}
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const VERSION_OPTIONS: {
  value: AppVersion;
  label: string;
  description: string;
  icon: IconName;
}[] = [
  {
    value: 'v1',
    label: 'Coco',
    description: 'The new AI-native experience',
    icon: 'sparkles',
  },
  {
    value: 'classic',
    label: 'Enterprise',
    description: 'The classic data workspace',
    icon: 'building-office',
  },
];

const SwitchAppVersionAskView: React.FC<{
  currentVersion: AppVersion;
  onRespond: (params: Params) => Promise<void>;
}> = ({ currentVersion, onRespond }) => {
  const [selectedVersion, setSelectedVersion] =
    useState<AppVersion>(currentVersion);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the option list so arrow keys / Enter work immediately
    containerRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    if (selectedVersion === currentVersion) {
      return;
    }
    setLoading(true);
    await onRespond({ version: selectedVersion });
    setLoading(false);
  };

  const moveSelection = (delta: number) => {
    const currentIndex = VERSION_OPTIONS.findIndex(
      (o) => o.value === selectedVersion
    );
    const next =
      (currentIndex + delta + VERSION_OPTIONS.length) % VERSION_OPTIONS.length;
    setSelectedVersion(VERSION_OPTIONS[next]!.value);
  };

  return (
    <ModalPageView
      title="Switch App Version"
      subtitle="Choose which version of the app to use"
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          await handleSubmit();
        }}
      >
        <div
          ref={containerRef}
          role="radiogroup"
          tabIndex={0}
          className="flex flex-col gap-1.5 outline-none"
          onKeyDown={async (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
              e.preventDefault();
              moveSelection(1);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
              e.preventDefault();
              moveSelection(-1);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              await handleSubmit();
            }
          }}
        >
          {VERSION_OPTIONS.map((option) => {
            const isSelected = selectedVersion === option.value;
            const isCurrent = currentVersion === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={-1}
                onClick={() => setSelectedVersion(option.value)}
                className={cn(
                  'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
                  'hover:bg-accent/40',
                  isSelected ? 'border-primary bg-accent' : 'border-transparent'
                )}
              >
                <Icon
                  icon={option.icon}
                  size="large"
                  className={cn(
                    'shrink-0 transition-colors',
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  )}
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium leading-tight">
                    {option.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {option.description}
                  </span>
                </div>
                {isCurrent && (
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="w-full flex justify-end gap-2">
          <Button
            type="submit"
            disabled={loading || selectedVersion === currentVersion}
            variant="default"
            className="w-fit self-end"
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Switch Version'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
